import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, X, ImagePlus } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useQueryClient } from '@tanstack/react-query';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { LoginArea } from '@/components/auth/LoginArea';
import { PowMiningBar } from '@/components/stashstr/PowMiningBar';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { mineThenPublish } from '@/lib/publishPow';
import {
  BODY_MAX,
  IMAGE_MAX_MB,
  IMAGES_MAX,
  POLL_MAX_OPTIONS,
  POST_POW,
  RELAYS,
  TITLE_MAX,
  TITLE_MIN,
  TERRITORIES,
  sanitizeUrl,
} from '@/lib/stashstr';

type PostTab = 'link' | 'discussion' | 'poll';

interface StagedImage {
  url: string;
  tags: string[][];
}

export function PostForm() {
  return (
    <Card>
      <CardContent className="pt-6">
        <Tabs defaultValue="discussion">
          <TabsList className="mb-4">
            <TabsTrigger value="discussion" className="text-[13px]">
              discussion
            </TabsTrigger>
            <TabsTrigger value="link" className="text-[13px]">
              link
            </TabsTrigger>
            <TabsTrigger value="poll" className="text-[13px]">
              poll
            </TabsTrigger>
          </TabsList>
          <TabsContent value="discussion">
            <Form mode="discussion" />
          </TabsContent>
          <TabsContent value="link">
            <Form mode="link" />
          </TabsContent>
          <TabsContent value="poll">
            <Form mode="poll" />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function Form({ mode }: { mode: PostTab }) {
  return mode === 'poll' ? <PollFields /> : <PostFields mode={mode} />;
}

function PostFields({ mode }: { mode: Exclude<PostTab, 'poll'> }) {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const publish = useNostrPublish();
  const upload = useUploadFile();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  const [territory, setTerritory] = useState('');
  const [customTerritory, setCustomTerritory] = useState('');
  const [url, setUrl] = useState('');
  const [body, setBody] = useState('');
  const [images, setImages] = useState<StagedImage[]>([]);
  const [uploading, setUploading] = useState(false);
  const [mining, setMining] = useState(false);
  const [tries, setTries] = useState(0);
  const [miningStart, setMiningStart] = useState(0);
  const cancelRef = useRef(false);
  const [error, setError] = useState<string | undefined>();

  const effectiveTerritory = customTerritory.trim().toLowerCase() || territory;

  const addImages = async (files: FileList | null) => {
    if (!files) return;
    setError(undefined);
    for (const file of Array.from(files)) {
      if (images.length >= IMAGES_MAX) {
        setError(`Max ${IMAGES_MAX} images per post.`);
        break;
      }
      if (file.size > IMAGE_MAX_MB * 1024 * 1024) {
        setError(`Image exceeds ${IMAGE_MAX_MB}MB limit.`);
        continue;
      }
      setUploading(true);
      try {
        const tags = await upload.mutateAsync(file);
        const urlTag = tags.find(([t]) => t === 'url')?.[1];
        if (urlTag) {
          setImages((prev) => [...prev, { url: urlTag, tags }]);
        }
      } catch {
        setError('Image upload failed. Try again.');
      } finally {
        setUploading(false);
      }
    }
  };

  const submit = async () => {
    setError(undefined);
    const t = title.trim();
    if (t.length < TITLE_MIN || t.length > TITLE_MAX) {
      setError(`Title must be ${TITLE_MIN}-${TITLE_MAX} characters.`);
      return;
    }
    if (!effectiveTerritory) {
      setError('Pick a territory.');
      return;
    }
    if (mode === 'link') {
      if (!sanitizeUrl(url)) {
        setError('Enter a valid https:// or http:// URL.');
        return;
      }
    }
    if (mode === 'discussion' && !body.trim() && images.length === 0) {
      setError('Discussion posts need some text or an image.');
      return;
    }

    // Build content: body + image URLs appended
    const contentParts: string[] = [];
    if (body.trim()) contentParts.push(body.trim());
    for (const img of images) contentParts.push(img.url);
    const content = contentParts.join('\n\n');

    // Tags
    const tags: string[][] = [['title', t], ['t', effectiveTerritory]];
    if (mode === 'link') {
      const safe = sanitizeUrl(url);
      if (safe) tags.push(['r', safe]);
    }
    for (const img of images) {
      tags.push(['imeta', `url ${img.url}`, 'm image/*']);
    }

    if (!user) {
      setError('Must be logged in.');
      return;
    }

    // Mine + publish via helper (keeps mined id == published id)
    setMining(true);
    cancelRef.current = false;
    setTries(0);
    setMiningStart(Date.now());
    try {
      const event = await mineThenPublish({
        userPubkey: user.pubkey,
        publish: publish.mutateAsync,
        kind: 11,
        content,
        tags,
        difficulty: POST_POW,
        onProgress: setTries,
        shouldCancelRef: cancelRef,
      });
      // Refresh feeds so /new shows the post immediately
      queryClient.invalidateQueries({ queryKey: ['stashstr', 'feed'] });
      const nevent = nip19.neventEncode({ id: event.id, relays: RELAYS, author: event.pubkey });
      navigate(`/items/${nevent}`);
    } catch {
      setError('Failed to publish. Try again.');
    } finally {
      setMining(false);
      setTries(0);
    }
  };

  const uploadingAny = uploading || publish.isPending || mining;

  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="post-title" className="text-[12px] text-gray-600 dark:text-gray-400">
          title <span className="text-gray-400 dark:text-gray-500">({title.length}/{TITLE_MAX})</span>
        </Label>
        <Input
          id="post-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={TITLE_MAX}
          className="text-[14px] mt-1"
          placeholder={`${TITLE_MIN}-${TITLE_MAX} characters`}
        />
      </div>

      <div>
        <Label htmlFor="post-territory" className="text-[12px] text-gray-600 dark:text-gray-400">
          territory
        </Label>
        <div className="flex gap-2 mt-1">
          <select
            id="post-territory"
            value={territory}
            onChange={(e) => {
              setTerritory(e.target.value);
              setCustomTerritory('');
            }}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-[13px]"
          >
            <option value="">choose…</option>
            {TERRITORIES.map((t) => (
              <option key={t} value={t}>
                ~{t}
              </option>
            ))}
          </select>
          <Input
            value={customTerritory}
            onChange={(e) => {
              setCustomTerritory(e.target.value);
              if (e.target.value) setTerritory('');
            }}
            placeholder="or custom…"
            className="text-[13px] w-40"
            aria-label="Custom territory"
          />
        </div>
      </div>

      {mode === 'link' && (
        <div>
          <Label htmlFor="post-url" className="text-[12px] text-gray-600 dark:text-gray-400">
            url
          </Label>
          <Input
            id="post-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/article"
            className="text-[13px] mt-1"
            inputMode="url"
          />
        </div>
      )}

      <div>
        <Label htmlFor="post-body" className="text-[12px] text-gray-600 dark:text-gray-400">
          body <span className="text-gray-400 dark:text-gray-500">({body.length}/{BODY_MAX})</span>
        </Label>
        <Textarea
          id="post-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={BODY_MAX}
          className="text-[13px] min-h-[100px] mt-1"
          placeholder={mode === 'link' ? 'Optional description…' : 'What do you want to discuss?'}
        />
      </div>

      <div>
        <Label className="text-[12px] text-gray-600 dark:text-gray-400">
          images (max {IMAGES_MAX}, {IMAGE_MAX_MB}MB each)
        </Label>
        <div className="flex flex-wrap gap-2 mt-1 items-center">
          {images.map((img, i) => (
            <div key={`${img.url}-${i}`} className="relative">
              <img
                src={sanitizeUrl(img.url)}
                alt=""
                className="w-16 h-16 object-cover rounded border border-gray-200 dark:border-[#2a2d30]"
              />
              <button
                type="button"
                onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))}
                className="absolute -top-1.5 -right-1.5 bg-white dark:bg-[#232528] border border-gray-300 dark:border-[#3a3d41] rounded-full p-0.5"
                aria-label="Remove image"
              >
                <X className="size-3" />
              </button>
            </div>
          ))}
          {images.length < IMAGES_MAX && (
            <label className="flex items-center justify-center w-16 h-16 border border-dashed border-gray-300 dark:border-[#3a3d41] rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1a1c1e]">
              <ImagePlus className="size-5 text-gray-400 dark:text-gray-500" />
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => addImages(e.target.files)}
                aria-label="Add image"
              />
            </label>
          )}
          {uploading && <Loader2 className="size-4 animate-spin text-gray-400 dark:text-gray-500" />}
        </div>
      </div>

      {error && <p className="text-[12px] text-red-600 dark:text-red-400">{error}</p>}

      {mining ? (
        <PowMiningBar
          tries={tries}
          difficulty={POST_POW}
          startedAt={miningStart}
          onCancel={() => {
            cancelRef.current = true;
          }}
        />
      ) : (
        <Button
          type="button"
          onClick={submit}
          disabled={publish.isPending}
          className="h-9 px-4 text-[13px] bg-[#f97316] hover:bg-[#ea580c] dark:bg-[#fb923c] dark:hover:bg-[#fdba74] dark:text-[#431407] text-white"
        >
          {publish.isPending ? <Loader2 className="size-4 animate-spin mr-1" /> : null}
          Mine + Post Poll (21-bit, ~2–5 min)
        </Button>
      )}
    </div>
  );
}

function PollFields() {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const publish = useNostrPublish();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  const [territory, setTerritory] = useState('');
  const [customTerritory, setCustomTerritory] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [mining, setMining] = useState(false);
  const [tries, setTries] = useState(0);
  const [miningStart, setMiningStart] = useState(0);
  const cancelRef = useRef(false);
  const [error, setError] = useState<string | undefined>();

  const effectiveTerritory = customTerritory.trim().toLowerCase() || territory;

  const submit = async () => {
    setError(undefined);
    const t = title.trim();
    if (t.length < TITLE_MIN || t.length > TITLE_MAX) {
      setError(`Question must be ${TITLE_MIN}-${TITLE_MAX} characters.`);
      return;
    }
    if (!effectiveTerritory) {
      setError('Pick a territory.');
      return;
    }
    const cleaned = options.map((o) => o.trim()).filter(Boolean);
    if (cleaned.length < 2) {
      setError('Poll needs at least 2 options.');
      return;
    }
    if (cleaned.length > POLL_MAX_OPTIONS) {
      setError(`Max ${POLL_MAX_OPTIONS} options.`);
      return;
    }
    if (!user) {
      setError('Must be logged in.');
      return;
    }

    // 1. Publish poll event (kind 1068) — no PoW on polls themselves
    let pollId: string;
    try {
      const pollEvent = await publish.mutateAsync({
        kind: 1068,
        content: t,
        tags: [
          ['polltype', 'single'],
          ...cleaned.map((o, i) => ['option', String(i + 1), o]),
        ],
      });
      pollId = pollEvent.id;
    } catch {
      setError('Failed to create poll. Try again.');
      return;
    }

    // 2. Mine + publish post referencing the poll
    setMining(true);
    cancelRef.current = false;
    setTries(0);
    setMiningStart(Date.now());
    try {
      const event = await mineThenPublish({
        userPubkey: user.pubkey,
        publish: publish.mutateAsync,
        kind: 11,
        content: '',
        tags: [
          ['title', t],
          ['t', effectiveTerritory],
          ['poll', pollId],
        ],
        difficulty: POST_POW,
        onProgress: setTries,
        shouldCancelRef: cancelRef,
      });
      // Refresh feeds so /new shows the post immediately
      queryClient.invalidateQueries({ queryKey: ['stashstr', 'feed'] });
      const nevent = nip19.neventEncode({ id: event.id, relays: RELAYS, author: event.pubkey });
      navigate(`/items/${nevent}`);
    } catch {
      setError('Failed to publish post. Try again.');
    } finally {
      setMining(false);
      setTries(0);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label htmlFor="poll-title" className="text-[12px] text-gray-600 dark:text-gray-400">
          question <span className="text-gray-400 dark:text-gray-500">({title.length}/{TITLE_MAX})</span>
        </Label>
        <Input
          id="poll-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={TITLE_MAX}
          className="text-[14px] mt-1"
          placeholder={`${TITLE_MIN}-${TITLE_MAX} characters`}
        />
      </div>

      <div>
        <Label htmlFor="poll-territory" className="text-[12px] text-gray-600 dark:text-gray-400">
          territory
        </Label>
        <div className="flex gap-2 mt-1">
          <select
            id="poll-territory"
            value={territory}
            onChange={(e) => {
              setTerritory(e.target.value);
              setCustomTerritory('');
            }}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-[13px]"
          >
            <option value="">choose…</option>
            {TERRITORIES.map((t) => (
              <option key={t} value={t}>
                ~{t}
              </option>
            ))}
          </select>
          <Input
            value={customTerritory}
            onChange={(e) => {
              setCustomTerritory(e.target.value);
              if (e.target.value) setTerritory('');
            }}
            placeholder="or custom…"
            className="text-[13px] w-40"
            aria-label="Custom territory"
          />
        </div>
      </div>

      <div>
        <Label className="text-[12px] text-gray-600 dark:text-gray-400">
          options (2-{POLL_MAX_OPTIONS}, single choice)
        </Label>
        <div className="space-y-2 mt-1">
          {options.map((opt, i) => (
            <div key={i} className="flex gap-2">
              <Input
                value={opt}
                onChange={(e) => {
                  const next = [...options];
                  next[i] = e.target.value;
                  setOptions(next);
                }}
                maxLength={80}
                placeholder={`Option ${i + 1}`}
                className="text-[13px]"
                aria-label={`Option ${i + 1}`}
              />
              {options.length > 2 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setOptions((prev) => prev.filter((_, j) => j !== i))}
                  aria-label={`Remove option ${i + 1}`}
                >
                  <X className="size-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
        {options.length < POLL_MAX_OPTIONS && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setOptions((prev) => [...prev, ''])}
            className="mt-2 text-[12px]"
          >
            + add option
          </Button>
        )}
      </div>

      {error && <p className="text-[12px] text-red-600 dark:text-red-400">{error}</p>}

      {mining ? (
        <PowMiningBar
          tries={tries}
          difficulty={POST_POW}
          startedAt={miningStart}
          onCancel={() => {
            cancelRef.current = true;
          }}
        />
      ) : (
        <Button
          type="button"
          onClick={submit}
          disabled={uploadingAny}
          className="h-9 px-4 text-[13px] bg-[#f97316] hover:bg-[#ea580c] dark:bg-[#fb923c] dark:hover:bg-[#fdba74] dark:text-[#431407] text-white"
        >
          {publish.isPending ? <Loader2 className="size-4 animate-spin mr-1" /> : null}
          Mine + Post (21-bit, ~2–5 min)
        </Button>
      )}
    </div>
  );
}

export default PostForm;
